import React, { useState } from 'react';
import moment from 'moment';

interface DataAvailability {
  date: string;
  hasSleepData: boolean;
  hasVitalSignsData: boolean;
}

interface CustomDatePickerProps {
  onDateChange?: (date: string) => void;
  initialDate?: string;
  deviceId?: string;
  dataAvailability?: DataAvailability[];
  onCalendarOpen?: () => void;
}

/**
 * Custom Date Picker with data availability indicators
 * Shows a single indicator for dates with Sleep or Vital Signs data
 */
export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  onDateChange,
  initialDate,
  dataAvailability = [],
  onCalendarOpen
}) => {
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(
    moment(initialDate || moment().format('YYYY-MM-DD'))
  );
  const [currentMonth, setCurrentMonth] = useState<moment.Moment>(
    moment(initialDate || moment().format('YYYY-MM-DD'))
  );
  const [isOpen, setIsOpen] = useState(false);

  // Get data availability for a specific date
  const getDataForDate = (date: moment.Moment): DataAvailability | undefined => {
    const dateStr = date.format('YYYY-MM-DD');
    return dataAvailability.find(d => d.date === dateStr);
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    const days: moment.Moment[] = [];
    let day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  };

  // Handle date selection
  const handleDateSelect = (date: moment.Moment) => {
    // Don't allow future dates
    if (date.isAfter(moment(), 'day')) {
      return;
    }

    setSelectedDate(date);
    setIsOpen(false);

    const formattedDate = date.format('YYYY-MM-DD');

    // Dispatch event for other components to listen
    document.dispatchEvent(
      new CustomEvent('datepicker-range-end', {
        detail: formattedDate,
      })
    );

    if (onDateChange) {
      onDateChange(formattedDate);
    }
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
  };

  const goToNextMonth = () => {
    // Don't allow navigating to future months
    if (currentMonth.clone().add(1, 'month').isAfter(moment(), 'month')) {
      return;
    }
    setCurrentMonth(currentMonth.clone().add(1, 'month'));
  };

  const goToPreviousYear = () => {
    setCurrentMonth(currentMonth.clone().subtract(1, 'year'));
  };

  const goToNextYear = () => {
    // Don't allow navigating to future years
    if (currentMonth.clone().add(1, 'year').isAfter(moment(), 'year')) {
      return;
    }
    setCurrentMonth(currentMonth.clone().add(1, 'year'));
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="relative">
      {/* Date Input */}
      <div className="relative">
        <input
          type="text"
          value={selectedDate.format('YYYY/MM/DD')}
          onClick={() => {
            if (!isOpen && onCalendarOpen) {
              onCalendarOpen();
            }
            setIsOpen(!isOpen);
          }}
          readOnly
          className="w-full px-4 py-3 pr-10 text-gray-900 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Calendar Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Calendar */}
          <div className="absolute z-50 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80">
            {/* Header with navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={goToPreviousYear}
                className="p-1 hover:bg-gray-100 rounded"
                type="button"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToPreviousMonth}
                className="p-1 hover:bg-gray-100 rounded"
                type="button"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">
                  {currentMonth.format('YYYY')}
                </div>
                <div className="text-sm text-gray-600">
                  {currentMonth.format('MMMM')}
                </div>
              </div>

              <button
                onClick={goToNextMonth}
                disabled={currentMonth.clone().add(1, 'month').isAfter(moment(), 'month')}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={goToNextYear}
                disabled={currentMonth.clone().add(1, 'year').isAfter(moment(), 'year')}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Week days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                const isCurrentMonth = day.month() === currentMonth.month();
                const isToday = day.isSame(moment(), 'day');
                const isSelected = day.isSame(selectedDate, 'day');
                const isFuture = day.isAfter(moment(), 'day');
                const dataInfo = getDataForDate(day);

                return (
                  <button
                    key={index}
                    onClick={() => handleDateSelect(day)}
                    disabled={isFuture}
                    type="button"
                    className={`
                      relative h-10 flex flex-col items-center justify-center rounded-lg text-sm
                      ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
                      ${isToday ? 'font-bold' : ''}
                      ${isSelected ? 'bg-blue-500 text-white font-semibold' : 'hover:bg-gray-100'}
                      ${isFuture ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                      transition-colors
                    `}
                  >
                    <span className="relative z-10">{day.format('D')}</span>
                    
                    {/* Data availability indicators */}
                    {dataInfo && (dataInfo.hasSleepData || dataInfo.hasVitalSignsData) && (
                      <div className="absolute bottom-1 flex gap-0.5">
                        <div 
                          className="w-1.5 h-1.5 rounded-full bg-orange-500" 
                          title="Data Available (Sleep or Vital Signs)" 
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-600 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span>Data Available</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
